import React, { useEffect, useState, useRef, useCallback } from "react";
import { IframeModalView } from "../IframeModalView";
import { useAppSelector, useAppDispatch } from "hooks";
import { closeIframeModal, openIframeModal, setIsIframeRefreshing } from "state/reducers/layoutSlice";
import "./index.scss";
import { IframeRefreshOverlay } from "./IframeRefreshOverlay";
import { 
  useGetTestScoresAndPerformanceQuery,
  homeApi
} from "services/homeApi";
import { useLazyGetAiRecommendationsQuery } from "services/aiRecommendations";
import { studyPlanner } from "services/studyPlanner";
import { decodeToken } from "utils/functions/decodeToken";
import { isScoreReport } from "utils/functions/isScoreReport";

export const IframeModalManager = () => {
  const dispatch = useAppDispatch();
  const { iframeModal, aiMCATChatDrawerStatus } = useAppSelector((state) => state.layout);
  const { mode } = useAppSelector((state) => state.theme);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const lastPushedStateRef = useRef<string | null>(null);
  const wasOpenRef = useRef(false);
  const lastModalRef = useRef<{ url?: string | null; title?: string | null }>({
    url: null,
    title: null,
  });
  
  const payload = decodeToken();

  // Only endpoints with no providesTags need explicit refetch/trigger;
  // all other endpoints are refreshed via invalidateTags.
  const { refetch: testScoresRefetch } = useGetTestScoresAndPerformanceQuery(undefined, { skip: !payload });
  const [triggerRefetchAiRecommendation] = useLazyGetAiRecommendationsQuery();

  // Handle browser history management for modal
  useEffect(() => {
    if (!iframeModal.isOpen || !iframeModal.url) {
      return;
    }

    const statePayload = {
      modalOpen: true,
      iframeUrl: iframeModal.url,
      iframeTitle: iframeModal.title,
    };

    const payloadSignature = JSON.stringify(statePayload);

    if (lastPushedStateRef.current === payloadSignature) {
      return;
    }

    // Push a new history entry when modal opens with metadata
    window.history.pushState(statePayload, '', window.location.href);
    lastPushedStateRef.current = payloadSignature;
  }, [iframeModal.isOpen, iframeModal.url, iframeModal.title]);

  useEffect(() => {
    if (!iframeModal.isOpen) {
      lastPushedStateRef.current = null;
    }
  }, [iframeModal.isOpen]);

  // Handle browser back navigation
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const modalState = (event.state || {}) as {
        modalOpen?: boolean;
        iframeUrl?: string;
        iframeTitle?: string;
      };

      if (modalState.modalOpen) {
        event.preventDefault();

        if (iframeModal.isOpen) {
          dispatch(closeIframeModal());
          window.history.pushState(null, '', window.location.href);
        } else if (modalState.iframeUrl) {
          dispatch(openIframeModal({ url: modalState.iframeUrl, title: modalState.iframeTitle }));
        }

        return;
      }

      if (iframeModal.isOpen) {
        // Prevent default navigation and close the modal instead
        event.preventDefault();
        dispatch(closeIframeModal());
        // Stay on current page by pushing the current state back
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [iframeModal.isOpen, dispatch]);

  const handleAutoRefresh = useCallback(async (targetUrl?: string | null) => {
    const urlToRefresh = targetUrl || iframeModal.url;
    if (!urlToRefresh) return;

    const MIN_REFRESH_DISPLAY_MS = 1500;
    // Cap is sized for the slowest endpoint that backs a visible view
    // (getOverallProgress has been observed at ~10s after invalidation). Too low and the
    // overlay would hide while a still-pending query leaves its cache stale.
    const MAX_REFRESH_WAIT_MS = 12000;

    setIsRefreshing(true);
    dispatch(setIsIframeRefreshing(true));

    // Invalidate tagged caches — RTK Query automatically refetches any mounted subscribers.
    // Covers Coursework, CourseIndex, OverallProgress, Practice, and StudyPlanner endpoints.
    dispatch(homeApi.util.invalidateTags([
      "Coursework",
      "CourseIndex",
      "OverallProgress",
      "Practice",
    ]));
    dispatch(studyPlanner.util.invalidateTags([
      "StudyPlanner",
      "StudyPlannerPostForm",
      "StudyPlannerUpdateEvent",
    ]));

    // Fire endpoints without providesTags so they also refresh; intentionally NOT awaited
    // because they don't drive the just-closed item's status.
    testScoresRefetch();
    triggerRefetchAiRecommendation();

    // Snapshot every in-flight query kicked off by the invalidations above, then wait
    // for all of them to settle. The same item can be represented in multiple endpoints
    // (e.g. getCoursework AND getOverallProgress), each backing a different view;
    // releasing on the first to return would leave slower-matching endpoints' caches
    // stale for any view they back.
    const runningQueries = dispatch(homeApi.util.getRunningQueriesThunk());
    const waitForAllSettled = new Promise<void>((resolve) => {
      if (runningQueries.length === 0) {
        resolve();
        return;
      }
      let remaining = runningQueries.length;
      const settleOne = () => {
        remaining -= 1;
        if (remaining === 0) resolve();
      };
      runningQueries.forEach((queryPromise: Promise<any>) => {
        queryPromise.then(settleOne, settleOne);
      });
    });

    const minDisplayTime = new Promise<void>((resolve) => setTimeout(resolve, MIN_REFRESH_DISPLAY_MS));
    const maxWait = new Promise<void>((resolve) => setTimeout(resolve, MAX_REFRESH_WAIT_MS));

    try {
      // Hold the overlay at least MIN_REFRESH_DISPLAY_MS, then release when every
      // captured in-flight query has settled — but never exceed MAX_REFRESH_WAIT_MS.
      await Promise.all([
        Promise.race([waitForAllSettled, maxWait]),
        minDisplayTime,
      ]);
    } catch (error) {
      console.error("Auto-refresh failed:", error);
    } finally {
      setIsRefreshing(false);
      dispatch(setIsIframeRefreshing(false));
    }
  }, [
    testScoresRefetch,
    triggerRefetchAiRecommendation,
    dispatch,
    iframeModal.url,
  ]);

  const handleClose = () => {
    // Close modal immediately for better UX
    dispatch(closeIframeModal());

    // Clean up history state - replace the modal entry instead of leaving it in the stack
    // This prevents the modal from reopening when user presses browser back button
    const currentState = window.history.state;
    if (currentState && currentState.modalOpen) {
      window.history.replaceState(null, '', window.location.href);
    }
  };

  // Track last open modal details for post-close refresh decisions
  useEffect(() => {
    if (iframeModal.isOpen) {
      lastModalRef.current = {
        url: iframeModal.url,
        title: iframeModal.title,
      };
    }
  }, [iframeModal.isOpen, iframeModal.url, iframeModal.title]);

  // Centralized auto-refresh trigger when modal transitions from open -> closed
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    const isNowOpen = iframeModal.isOpen;

    if (wasOpen && !isNowOpen) {
      const { url, title } = lastModalRef.current;
      if (!isScoreReport(url, title)) {
        handleAutoRefresh(url).catch((error: unknown) => {
          console.error("Background auto-refresh failed:", error);
        });
      } else {
        console.log("Skipping auto-refresh for score report");
      }
    }

    wasOpenRef.current = isNowOpen;
  }, [iframeModal.isOpen]);

  return (
    <>
      <IframeModalView
        isOpen={iframeModal.isOpen}
        onClose={handleClose}
        url={iframeModal.url}
        theme={mode}
        title={iframeModal.title}
        aiChatDrawerStatus={aiMCATChatDrawerStatus}
      />

      <IframeRefreshOverlay
        isRefreshing={isRefreshing}
        aiChatDrawerStatus={aiMCATChatDrawerStatus}
        theme={mode}
      />
    </>
  );
};
